/*
 * <complementary-addon>
 * Inline add-on selector for a complementary product (e.g. Installation) on the product page.
 *
 *  - "Phase" option (values containing "Single Phase" / "Three Phase") -> selectable pills.
 *  - The other option -> selectable cards (option text + price for the chosen phase).
 *  - Phase auto-syncs from the charger's own phase option (matched by value text).
 *  - Selecting a card shows "+ $price" by the main price and adds that variant to the cart
 *    when the main product is added (intercepts the product form submit in capture phase).
 */
if (!customElements.get('complementary-addon')) {
  const CHECK_SVG =
    '<svg class="icon icon-checkmark" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 9" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"/></svg>';
  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const PHASE_RE = /phase/i;
  const THREE_RE = /three\s*phase/i;
  const SINGLE_RE = /single\s*phase/i;

  customElements.define(
    'complementary-addon',
    class ComplementaryAddon extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;
        try {
          this.data = JSON.parse(this.querySelector('[data-addon-data]').textContent);
        } catch (e) {
          this.data = { options: [], variants: [] };
        }
        this.phaseEl = this.querySelector('[data-phase]');
        this.optionsEl = this.querySelector('[data-options]');

        const opts = this.data.options || [];
        this.phaseOption = opts.find((o) => o.values.some((v) => PHASE_RE.test(v))) || null;
        const others = opts.filter((o) => o !== this.phaseOption);
        this.cardsOption = others.length ? others[0] : this.phaseOption;
        this.usePills = !!(this.phaseOption && this.cardsOption && this.cardsOption !== this.phaseOption);

        this.selectedPhase = this.usePills ? this.phaseOption.values[0] : null;
        this.selectedType = null;

        this.renderPhase();
        this.renderCards();

        // Add the selected add-on to cart when the main product form is submitted.
        this.onSubmit = (event) => {
          const form = document.getElementById('product-form-' + this.sectionId);
          if (!form || event.target !== form) return;
          const variant = this.selectedVariant();
          if (!variant || this._adding) return;
          event.preventDefault();
          event.stopPropagation();
          this.addThenSubmit(form, variant.id);
        };
        document.addEventListener('submit', this.onSubmit, true);

        this.bindChargerPhase();
      }

      disconnectedCallback() {
        if (this.onSubmit) document.removeEventListener('submit', this.onSubmit, true);
        if (this._chargerVs && this._chargerHandler) this._chargerVs.removeEventListener('change', this._chargerHandler);
      }

      variantFor(phase, type) {
        return (this.data.variants || []).find((v) => {
          if (this.usePills && this.phaseOption && v.options[this.phaseOption.position - 1] !== phase) return false;
          if (this.cardsOption && v.options[this.cardsOption.position - 1] !== type) return false;
          return true;
        });
      }

      selectedVariant() {
        if (!this.selectedType) return null;
        return this.variantFor(this.selectedPhase, this.selectedType);
      }

      // ---- phase pills ----
      renderPhase() {
        if (!this.usePills) {
          this.phaseEl.hidden = true;
          return;
        }
        this.phaseEl.hidden = false;
        this.phaseEl.replaceChildren(
          ...this.phaseOption.values.map((val) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'comp-addon__pill' + (val === this.selectedPhase ? ' comp-addon__pill--on' : '');
            b.textContent = val;
            b.setAttribute('aria-pressed', val === this.selectedPhase ? 'true' : 'false');
            b.addEventListener('click', () => this.selectPhase(val));
            return b;
          })
        );
      }

      selectPhase(val) {
        if (val === this.selectedPhase) return;
        this.selectedPhase = val;
        Array.from(this.phaseEl.children).forEach((b) => {
          const on = b.textContent === val;
          b.classList.toggle('comp-addon__pill--on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        this.renderCards(); // prices update for the new phase
        this.updatePriceNote();
      }

      // ---- type cards ----
      renderCards() {
        const values = this.cardsOption ? this.cardsOption.values : [];
        this.optionsEl.replaceChildren(
          ...values.map((typeVal) => {
            const variant = this.variantFor(this.selectedPhase, typeVal);
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'comp-addon__option';
            btn.dataset.type = typeVal;
            const available = variant && variant.available;
            if (variant) btn.dataset.variantId = variant.id;
            if (!available) {
              btn.disabled = true;
              btn.setAttribute('aria-disabled', 'true');
            }
            const on = typeVal === this.selectedType;
            if (on) btn.classList.add('comp-addon__option--on');
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
            const priceText = variant ? esc(variant.priceMoney) + (variant.available ? '' : ' · sold out') : '—';
            btn.innerHTML =
              '<span class="comp-addon__check" aria-hidden="true">' + CHECK_SVG + '</span>' +
              '<span class="comp-addon__option-text">' + esc(typeVal) + '</span>' +
              '<span class="comp-addon__option-price">' + priceText + '</span>';
            btn.addEventListener('click', () => this.selectType(typeVal, btn));
            return btn;
          })
        );
      }

      selectType(typeVal, btn) {
        if (btn.hasAttribute('disabled')) return;
        this.selectedType = this.selectedType === typeVal ? null : typeVal;
        Array.from(this.optionsEl.children).forEach((b) => {
          const on = b.dataset.type === this.selectedType;
          b.classList.toggle('comp-addon__option--on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        this.updatePriceNote();
      }

      // ---- price note next to the main price ----
      updatePriceNote() {
        const priceEl = document.getElementById('price-' + this.sectionId);
        if (!priceEl) return;
        const noteId = 'comp-addon-note-' + this.sectionId + '-' + this.productId;
        let note = document.getElementById(noteId);
        const variant = this.selectedVariant();
        if (!variant) {
          if (note) note.remove();
        } else {
          if (!note) {
            note = document.createElement('span');
            note.id = noteId;
            note.className = 'comp-addon-price-note';
            priceEl.insertAdjacentElement('afterend', note);
          }
          note.textContent = '+ ' + variant.priceMoney + ' ' + this.addonName();
        }
        const anyNote = priceEl.parentElement && priceEl.parentElement.querySelector('.comp-addon-price-note');
        priceEl.classList.toggle('comp-addon-active', !!anyNote);
      }

      addonName() {
        return (this.dataset.addonName || 'add-on').trim().toLowerCase();
      }

      // ---- auto-sync phase from the charger's own phase option ----
      bindChargerPhase() {
        if (!this.usePills) return;
        // The charger picker may be <variant-radios> (buttons/swatches) or <variant-selects> (dropdowns).
        const vs =
          document.querySelector(
            'variant-radios[data-section="' + this.sectionId + '"], variant-selects[data-section="' + this.sectionId + '"]'
          ) || document.getElementById('variant-selects-' + this.sectionId);
        if (!vs) return;
        this._chargerVs = vs;
        this.syncFromCharger();
        this._chargerHandler = () => this.syncFromCharger();
        vs.addEventListener('change', this._chargerHandler);
      }

      syncFromCharger() {
        const vs = this._chargerVs;
        if (!vs) return;
        const values = [];
        vs.querySelectorAll('select').forEach((s) => values.push(s.value));
        vs.querySelectorAll('input[type="radio"]:checked').forEach((i) => values.push(i.value));
        const text = values.join(' ');
        let match = null;
        if (THREE_RE.test(text)) match = this.phaseOption.values.find((v) => THREE_RE.test(v));
        else if (SINGLE_RE.test(text)) match = this.phaseOption.values.find((v) => SINGLE_RE.test(v));
        if (match) this.selectPhase(match);
      }

      // ---- cart ----
      async addThenSubmit(form, variantId) {
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        try {
          await fetch(addUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });
        } catch (e) {
          /* let the main product add proceed regardless */
        }
        this._adding = true;
        const btn = form.querySelector('[name="add"]') || form.querySelector('[type="submit"]');
        if (form.requestSubmit) form.requestSubmit(btn);
        else form.submit();
        setTimeout(() => {
          this._adding = false;
        }, 800);
      }
    }
  );
}
